/**
 * Canonical, property-scoped P&L totals computation.
 *
 * This module is the single source of truth for the on-screen Profit & Loss
 * math (revenue / expenses / net income for a month). Both the client P&L
 * surfaces (via `client/src/lib/pnl-profit.ts`, which re-exports
 * `computePnlProfitForMonth`) and the server-side Owner CRM financial roll-up
 * (`computeOwnerFinancialsRollup` in `server/routes/operations.ts`) compute
 * their numbers from here so the two surfaces can never drift apart for the
 * same owner and date range.
 *
 * The formula deliberately matches the on-screen P&L page
 * (`client/src/pages/app-financials/ProfitAndLossTab.tsx`):
 *
 *   revenue  = combinedTotalIncome = totalCollections + totalAdjustments + otherIncome
 *   expenses = totalExpensesWithPS = standardExpenses + nonBsCustomExpenses + charges
 *   net      = revenue − expenses
 *
 * PadSplit revenue is derived from the expense-bucket collection categories
 * (membership dues, late fees, etc.) and their notes fallback — the raw
 * `padsplit_earnings` income rows are deliberately excluded to avoid
 * double-counting. PadSplit booking/service fees are informational only and
 * are excluded from both income and expenses on this surface (mirroring the
 * on-screen P&L), so they have no net effect on profit here.
 */

import { parsePadsplitBreakdown } from "./padsplit-breakdown";

export type PnlExpenseRow = {
  category: string;
  amount: string;
  date: string;
  userId?: number | null;
  propertyId?: number | null;
  isCompanyLevel?: boolean | null;
  excludedAt?: string | Date | null;
  voidedAt?: string | Date | null;
  accountIncludedInPnl?: boolean | null;
};

export type PnlIncomeRow = {
  source: string;
  amount: string;
  date: string;
  notes?: string | null;
  userId?: number | null;
  propertyId?: number | null;
  isCompanyLevel?: boolean | null;
  excludedAt?: string | Date | null;
  voidedAt?: string | Date | null;
  accountIncludedInPnl?: boolean | null;
};

export type PnlCatSettings = {
  customCategories?: { label: string; value: string; isBalanceSheet?: boolean }[] | null;
};

export { parsePadsplitBreakdown };

// ── Category constants ───────────────────────────────────────────────────────

/**
 * Standard operating expense categories included in P&L Total expenses
 * (mirrors `expenseCategoryGroups` in app-financials.tsx).
 */
export const STANDARD_EXPENSE_CATS = new Set([
  "mortgage",
  "property_tax",
  "insurance",
  "hoa",
  "utilities_electric",
  "utilities_gas",
  "utilities_water",
  "utilities_sewer",
  "utilities_trash",
  "utilities_internet",
  "repairs_maintenance",
  "cleaning",
  "lawn_care",
  "pest_control",
  "property_management",
  "padsplit_mgmt_fee",
  "furnishings",
  "supplies",
  "legal_professional",
  "marketing",
  "travel",
]);

/**
 * Collection categories — their amounts come from expense records OR from
 * PadSplit notes-based fallback when that account/property/category has no
 * expense-bucket record for the month.
 */
const COLLECTION_CATS = [
  "membership_dues",
  "move_in_fees",
  "commitment_fees",
  "late_fees",
  "other_bills",
] as const;

type CollectionCat = (typeof COLLECTION_CATS)[number];

const INFORMATIONAL_FEE_CATS = [
  "padsplit_booking_fees",
  "padsplit_service_fees",
] as const;

type InformationalFeeCat = (typeof INFORMATIONAL_FEE_CATS)[number];
type PadsplitPnlCat = CollectionCat | InformationalFeeCat;
const INFORMATIONAL_FEE_CAT_SET = new Set<string>(INFORMATIONAL_FEE_CATS);

type NotesBD = {
  dues: number;
  moveInFees: number;
  commitmentFees: number;
  lateFees: number;
  otherBills: number;
  bookingFees: number;
  serviceFees: number;
};

const COLLECTION_NOTES_FIELD: Record<CollectionCat, keyof NotesBD> = {
  membership_dues: "dues",
  move_in_fees: "moveInFees",
  commitment_fees: "commitmentFees",
  late_fees: "lateFees",
  other_bills: "otherBills",
};

const PADSPLIT_NOTES_FIELD: Record<PadsplitPnlCat, keyof NotesBD> = {
  ...COLLECTION_NOTES_FIELD,
  padsplit_booking_fees: "bookingFees",
  padsplit_service_fees: "serviceFees",
};

const PADSPLIT_PNL_CATS = [
  ...COLLECTION_CATS,
  ...INFORMATIONAL_FEE_CATS,
] as const;

type ScopedPnlRow = {
  userId?: number | null;
  propertyId?: number | null;
  isCompanyLevel?: boolean | null;
};

function padsplitScopeKey(row: ScopedPnlRow): string {
  const account = row.userId == null ? "legacy" : `user:${row.userId}`;
  const property = row.isCompanyLevel
    ? "company"
    : row.propertyId == null
      ? "unassigned"
      : `property:${row.propertyId}`;
  return `${account}|${property}`;
}

function isExcludedPnlRow(
  row: {
    excludedAt?: string | Date | null;
    voidedAt?: string | Date | null;
    accountIncludedInPnl?: boolean | null;
  },
): boolean {
  return Boolean(row.excludedAt || row.voidedAt || row.accountIncludedInPnl === false);
}

/**
 * Resolve PadSplit categories for one month.
 *
 * Expense buckets take precedence independently for each account/property/
 * category tuple. Merely having a bucket is authoritative (including a bucket
 * whose sum is exactly zero); notes fill only categories not covered by a
 * bucket in that same tuple. Company-level and unassigned rows intentionally
 * have different scopes. The raw `padsplit_earnings.amount` is never used.
 */
export function resolvePnlPadsplitCategoriesForMonth(
  expenses: PnlExpenseRow[],
  income: PnlIncomeRow[],
  monthKey: string,
): Record<string, number> {
  const result = Object.fromEntries(PADSPLIT_PNL_CATS.map((cat) => [cat, 0])) as Record<
    PadsplitPnlCat,
    number
  >;
  const buckets = new Map<string, Map<PadsplitPnlCat, number>>();
  const notes = new Map<string, NotesBD>();

  for (const row of expenses) {
    if (!row.date.startsWith(monthKey) || isExcludedPnlRow(row)) continue;
    if (!(PADSPLIT_PNL_CATS as readonly string[]).includes(row.category)) continue;
    const scope = padsplitScopeKey(row);
    let scopedBuckets = buckets.get(scope);
    if (!scopedBuckets) {
      scopedBuckets = new Map();
      buckets.set(scope, scopedBuckets);
    }
    const category = row.category as PadsplitPnlCat;
    scopedBuckets.set(
      category,
      (scopedBuckets.get(category) ?? 0) + parseFloat(row.amount || "0"),
    );
  }

  for (const row of income) {
    if (
      row.source !== "padsplit_earnings" ||
      !row.date.startsWith(monthKey) ||
      isExcludedPnlRow(row)
    ) {
      continue;
    }
    const parsed = parsePadsplitBreakdown(row.notes);
    if (!parsed) continue;
    const scope = padsplitScopeKey(row);
    const scopedNotes = notes.get(scope) ?? {
      dues: 0,
      moveInFees: 0,
      commitmentFees: 0,
      lateFees: 0,
      otherBills: 0,
      bookingFees: 0,
      serviceFees: 0,
    };
    for (const field of Object.values(PADSPLIT_NOTES_FIELD)) {
      scopedNotes[field] += parsed[field];
    }
    notes.set(scope, scopedNotes);
  }

  const scopes = new Set([...buckets.keys(), ...notes.keys()]);
  for (const scope of scopes) {
    const scopedBuckets = buckets.get(scope);
    const scopedNotes = notes.get(scope);
    for (const category of PADSPLIT_PNL_CATS) {
      if (scopedBuckets?.has(category)) {
        const bucketAmount = scopedBuckets.get(category) ?? 0;
        // Fee buckets are stored as negative expenses, while parsed note fees
        // are positive informational magnitudes. Normalize each selected
        // account/property bucket before combining scopes.
        result[category] += INFORMATIONAL_FEE_CAT_SET.has(category)
          ? Math.abs(bucketAmount)
          : bucketAmount;
      } else {
        result[category] += scopedNotes?.[PADSPLIT_NOTES_FIELD[category]] ?? 0;
      }
    }
  }
  return result;
}

// ── Shared totals calculator ─────────────────────────────────────────────────

export type PnlMonthTotals = {
  /** combinedTotalIncome (collections + adjustments + other income). */
  revenue: number;
  /** totalExpensesWithPS (standard + non-BS custom + charges). */
  expenses: number;
  /** revenue − expenses. */
  netIncome: number;
};

/**
 * Compute the P&L revenue / expenses / net for a single month key (YYYY-MM)
 * over already-scoped rows. This is the authoritative formula — identical to
 * what the on-screen P&L page renders for each month column, and to the
 * previous standalone `computePnlProfitForMonth` (whose net is `netIncome`).
 *
 * @param filteredExpenses  Expenses already scoped to the target properties
 *                          and with split parents excluded.
 * @param filteredIncome    Income rows already scoped to the target properties
 *                          and with split parents excluded.
 * @param catSettings       Expense category settings (for balance-sheet /
 *                          custom-expense-category handling). May be undefined
 *                          while the query is loading; treated as "no custom
 *                          categories" in that case.
 * @param monthKey          YYYY-MM string for the target month.
 */
export function computePnlTotalsForMonth(
  filteredExpenses: PnlExpenseRow[],
  filteredIncome: PnlIncomeRow[],
  catSettings: PnlCatSettings | undefined,
  monthKey: string,
): PnlMonthTotals {
  const padsplitCategories = resolvePnlPadsplitCategoriesForMonth(
    filteredExpenses,
    filteredIncome,
    monthKey,
  );

  // ── combinedTotalIncome ────────────────────────────────────────────────

  // totalCollections: membership_dues, move_in_fees, commitment_fees,
  //   late_fees, other_bills — via expense records or notes fallback.
  const totalCollections = COLLECTION_CATS.reduce(
    (sum, cat) => sum + padsplitCategories[cat],
    0,
  );

  // totalAdjustments: credits + referral_rewards (always from expense records)
  const totalAdjustments = (["credits", "referral_rewards"] as const).reduce(
    (sum, cat) =>
      sum +
      filteredExpenses
        .filter(
          (e) =>
            e.category === cat &&
            e.date.startsWith(monthKey) &&
            !isExcludedPnlRow(e),
        )
        .reduce((s, e) => s + parseFloat(e.amount || "0"), 0),
    0,
  );

  // otherIncome: mercury_credit + manual income rows (NOT padsplit_earnings
  // — the expense-bucket breakdown is the authoritative PadSplit source per
  // Task #2301; including net paysplit_earnings would double-count).
  const otherIncome = filteredIncome
    .filter(
      (r) =>
        (r.source === "mercury_credit" || r.source === "manual" || r.source === "stripe_bank") &&
        r.date.startsWith(monthKey) &&
        !isExcludedPnlRow(r),
    )
    .reduce((s, r) => s + parseFloat(r.amount || "0"), 0);

  const combinedTotalIncome = totalCollections + totalAdjustments + otherIncome;

  // ── totalExpensesWithPS ────────────────────────────────────────────────

  // Non-balance-sheet custom categories appear in P&L Expenses.
  const nonBsCustomCatValues = new Set(
    ((catSettings?.customCategories || []) as {
      value: string;
      isBalanceSheet?: boolean;
    }[])
      .filter((c) => !c.isBalanceSheet)
      .map((c) => c.value),
  );

  // Inclusion-based filter (mirrors expenseCategoryGroups + padsplitChargeRows):
  //   standard grouped expense cats + non-BS custom expense cats + "charges"
  // Explicitly excluded (not in any of the above):
  //   "other" (uncategorized), "capex" (balance-sheet built-in),
  //   "padsplit_booking_fees" / "padsplit_service_fees" (informational-only),
  //   all collection + adjustment cats, all BS custom cats.
  const totalExpenses = filteredExpenses
    .filter((e) => {
      if (!e.date.startsWith(monthKey)) return false;
      if (isExcludedPnlRow(e)) return false;
      if (STANDARD_EXPENSE_CATS.has(e.category)) return true;
      if (e.category === "charges") return true; // padsplitChargeRows
      if (nonBsCustomCatValues.has(e.category)) return true;
      return false;
    })
    .reduce((s, e) => s + parseFloat(e.amount || "0"), 0);

  return {
    revenue: combinedTotalIncome,
    expenses: totalExpenses,
    netIncome: combinedTotalIncome - totalExpenses,
  };
}

/**
 * Sum the P&L totals across every month present in the supplied rows (or an
 * explicit set of YYYY-MM month keys). Used by the Owner CRM roll-up to report
 * a single all-time (or date-ranged) revenue / expenses / net per owner using
 * the exact same per-month formula the P&L page renders, so the two surfaces
 * agree for the same owner and date range.
 *
 * The per-month bucketing matters: the PadSplit notes fallback is decided
 * per-month (a month with no synced expense buckets falls back to parsing the
 * `padsplit_earnings` notes), so we cannot simply sum the whole range at once.
 *
 * @param monthKeys  Optional explicit month-key allow-list (YYYY-MM). When
 *                   omitted, every month that appears on an expense or income
 *                   row is included.
 */
export function computePnlTotals(
  filteredExpenses: PnlExpenseRow[],
  filteredIncome: PnlIncomeRow[],
  catSettings: PnlCatSettings | undefined,
  monthKeys?: Iterable<string>,
): PnlMonthTotals {
  const keys = monthKeys
    ? new Set(monthKeys)
    : new Set<string>([
        ...filteredExpenses.map((e) => (e.date || "").slice(0, 7)),
        ...filteredIncome.map((r) => (r.date || "").slice(0, 7)),
      ]);

  let revenue = 0;
  let expenses = 0;
  for (const key of keys) {
    if (!key) continue;
    const t = computePnlTotalsForMonth(filteredExpenses, filteredIncome, catSettings, key);
    revenue += t.revenue;
    expenses += t.expenses;
  }
  return { revenue, expenses, netIncome: revenue - expenses };
}
